import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'youtuber',
    emoji: '🎬',
    title: '유튜버',
    category: '크리에이터',
    color: 'from-red-400 to-rose-500',
    bg: 'bg-red-50',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
    story: `유튜버 지민은 채널을 막 시작했습니다.
처음엔 구독자가 500명이었는데, 꾸준히 영상을 올리니
매주 1,200명씩 늘고 있어요.
"구독자가 몇 주 후에 10,000명이 될까?" 지민은 궁금했어요.`,
    situation: '유튜브 채널 구독자 수: 처음 500명에서 시작해 매주 1,200명씩 증가합니다.',
    question: 'x주 후 구독자 수 y를 함수식으로 나타내고, 구독자가 13,100명이 되는 시점은?',
    xLabel: '주(x)',
    yLabel: '구독자 수(y)',
    equation: 'y = 1200x + 500',
    equationLabel: 'y = 1200x + 500',
    slopeVal: 1200,
    slopeDesc: '매주 +1,200명 (증가 속도)',
    interceptVal: 500,
    interceptDesc: '초기 구독자 500명 (y절편)',
    answer: 'y = 1200x + 500\n13,100 = 1200x + 500 → x = 10.5 → 약 11주 후',
    whyMath: '유튜버·크리에이터는 구독자 증가 추세를 1차 함수로 분석해 성장 전략을 수립합니다.',
    insight: '유튜브·인스타그램·틱톡 크리에이터는 구독자·팔로워 증가율을 함수로 분석해 콘텐츠 전략을 최적화합니다.',
    quiz: [
      { q: '5주 후 구독자 수는?', a: 'y = 1200×5 + 500 = 6,500명', hint: 'x=5 대입: y = 1200×5 + 500' },
      { q: '구독자가 8,900명이 되려면 몇 주?', a: '8900 = 1200x + 500 → x = 7주', hint: '8900 = 1200x + 500 → 1200x = 8400' },
    ],
    jobs: ['유튜버·크리에이터', '소셜미디어 마케터', '콘텐츠 전략가', '디지털 마케터'],
  },
  {
    id: 'soccer',
    emoji: '⚽',
    title: '축구선수',
    category: '스포츠',
    color: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `손흥민을 꿈꾸는 준서는 매일 학교 운동장에서 슈팅 연습을 합니다.
처음엔 10번 차면 3골을 넣었어요 (성공률 30%).
처음엔 매주 연습할수록 성공 횟수가 늘어났습니다.
그런데 매주 2골씩 더 넣을 수 있게 됐어요.
"이대로라면 언제쯤 10골을 넣을 수 있을까?"`,
    situation: '슈팅 성공 횟수: 처음 3골에서 시작해 매주 2골씩 증가합니다.',
    question: 'x주 후 슈팅 성공 횟수 y를 함수식으로 나타내고, 10골을 넣는 시점은?',
    xLabel: '주(x)',
    yLabel: '성공 횟수(y)',
    equation: 'y = 2x + 3',
    equationLabel: 'y = 2x + 3',
    slopeVal: 2,
    slopeDesc: '주당 +2골 (실력 향상 속도)',
    interceptVal: 3,
    interceptDesc: '초기 성공 3골 (y절편)',
    answer: 'y = 2x + 3\n10 = 2x + 3 → x = 3.5 → 약 4주 후',
    whyMath: '스포츠 분석가는 선수 성장 데이터를 1차 함수로 모델링해 훈련 계획을 수립합니다.',
    insight: '스포츠 구단·퍼포먼스 코치는 선수 성장 곡선을 1차 함수로 분석해 훈련 강도와 전략을 조정합니다.',
    quiz: [
      { q: '6주 후 슈팅 성공 횟수는?', a: 'y = 2×6 + 3 = 15골', hint: 'x=6 대입: y = 2×6 + 3' },
      { q: '성공 횟수가 21골이 되려면 몇 주?', a: '21 = 2x + 3 → x = 9주', hint: '21 = 2x + 3 → 2x = 18' },
    ],
    jobs: ['프로 축구선수', '스포츠 데이터 분석가', '퍼포먼스 코치', '스포츠 과학자'],
  },
  {
    id: 'chef',
    emoji: '👨‍🍳',
    title: '셰프',
    category: '요리',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    story: `파스타 장인을 꿈꾸는 현아는 자신만의 레스토랑을 열었습니다.
기본 준비 비용이 10,000원이고, 파스타 한 그릇에 2,500원 재료비가 들어요.
"파스타를 많이 팔면 비용이 어떻게 변할까?"
손익분기점을 계산해서 가게를 잘 운영하고 싶어요.`,
    situation: '파스타 재료비: 기본 준비 비용 10,000원 + 1인분당 2,500원.',
    question: 'x그릇 조리 시 총 비용 y를 함수식으로 나타내고, 비용이 20,000원이 되는 그릇 수는?',
    xLabel: '그릇 수(x)',
    yLabel: '총 비용(y, 원)',
    equation: 'y = 2500x + 10000',
    equationLabel: 'y = 2500x + 10000',
    slopeVal: 2500,
    slopeDesc: '그릇당 +2,500원 (재료비)',
    interceptVal: 10000,
    interceptDesc: '기본 준비 비용 10,000원 (y절편)',
    answer: 'y = 2500x + 10000\n20000 = 2500x + 10000 → x = 4그릇',
    whyMath: '요리사·식당 운영자는 재료비·수익을 1차 함수로 계산해 메뉴 가격과 손익분기점을 결정합니다.',
    insight: '레스토랑·카페 운영자는 재료비·인건비·수익을 1차 함수로 분석해 최적의 가격 전략을 수립합니다.',
    quiz: [
      { q: '파스타 8그릇 조리 시 총 비용은?', a: 'y = 2500×8 + 10000 = 30,000원', hint: 'x=8 대입' },
      { q: '총 비용이 35,000원이면 몇 그릇?', a: '35000 = 2500x + 10000 → x = 10그릇', hint: '2500x = 25000' },
    ],
    jobs: ['셰프·요리사', '식당 운영자', '식품 개발자', '영양사'],
  },
  {
    id: 'doctor',
    emoji: '👨‍⚕️',
    title: '의사',
    category: '의료',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `소아과 의사를 꿈꾸는 서연이는 성장 클리닉에서 실습 중입니다.
아기 키 데이터를 보니 태어날 때 50cm에서 시작해
매달 2.5cm씩 성장하고 있었어요.
"이 아기는 언제 100cm가 될까?" 서연이는 계산해보고 싶었어요.`,
    situation: '아기 키: 태어날 때 50cm에서 시작해 매달 2.5cm씩 성장합니다.',
    question: 'x달 후 키 y를 함수식으로 나타내고, 키가 75cm가 되는 시점은?',
    xLabel: '개월(x)',
    yLabel: '키(y, cm)',
    equation: 'y = 2.5x + 50',
    equationLabel: 'y = 2.5x + 50',
    slopeVal: 2.5,
    slopeDesc: '월 +2.5cm (성장 속도)',
    interceptVal: 50,
    interceptDesc: '출생 시 키 50cm (y절편)',
    answer: 'y = 2.5x + 50\n75 = 2.5x + 50 → x = 10 → 10개월 후',
    whyMath: '의사는 환자 성장·회복 데이터를 1차 함수로 분석해 진단과 치료 계획을 수립합니다.',
    insight: '소아과·성장클리닉·보건 연구소에서는 성장 데이터를 1차 함수로 모델링해 정상 발달 기준을 설정합니다.',
    quiz: [
      { q: '12개월 후 키는?', a: 'y = 2.5×12 + 50 = 80cm', hint: 'x=12 대입' },
      { q: '키가 87.5cm가 되려면?', a: '87.5 = 2.5x + 50 → x = 15개월', hint: '2.5x = 37.5' },
    ],
    jobs: ['소아과 의사', '성장 클리닉 전문의', '보건 연구원', '의료 데이터 분석가'],
  },
  {
    id: 'idol',
    emoji: '🎤',
    title: '아이돌',
    category: '연예',
    color: 'from-pink-400 to-fuchsia-500',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    badge: 'bg-pink-100 text-pink-700',
    story: `데뷔를 앞둔 아이돌 태양이는 연습을 거듭하고 있습니다.
안무 연습을 처음 시작할 때 완성도가 40점이었는데,
매주 연습하면서 5점씩 오르고 있어요.
"몇 주 후에 완성도 100점을 달성할 수 있을까?"`,
    situation: '안무 완성도: 처음 40점에서 시작해 매주 5점씩 향상됩니다.',
    question: 'x주 후 완성도 y를 함수식으로 나타내고, 완성도가 90점이 되는 시점은?',
    xLabel: '주(x)',
    yLabel: '완성도(y, 점)',
    equation: 'y = 5x + 40',
    equationLabel: 'y = 5x + 40',
    slopeVal: 5,
    slopeDesc: '주당 +5점 (실력 향상)',
    interceptVal: 40,
    interceptDesc: '초기 완성도 40점 (y절편)',
    answer: 'y = 5x + 40\n90 = 5x + 40 → x = 10 → 10주 후',
    whyMath: '엔터테인먼트 기획사는 연습생 성장 곡선을 1차 함수로 분석해 데뷔 시점을 결정합니다.',
    insight: 'K-POP 기획사는 연습생 평가 데이터를 1차 함수로 분석해 훈련 커리큘럼과 데뷔 일정을 최적화합니다.',
    quiz: [
      { q: '8주 후 안무 완성도는?', a: 'y = 5×8 + 40 = 80점', hint: 'x=8 대입' },
      { q: '완성도가 100점이 되려면?', a: '100 = 5x + 40 → x = 12주', hint: '5x = 60' },
    ],
    jobs: ['아이돌·가수', '엔터테인먼트 PD', '안무가', '음악 프로듀서'],
  },
  {
    id: 'detective',
    emoji: '🔍',
    title: '탐정',
    category: '추리',
    color: 'from-slate-500 to-gray-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    badge: 'bg-slate-100 text-slate-700',
    story: `수사관을 꿈꾸는 민준이는 CCTV 분석 실습 중입니다.
용의자가 처음 발견된 위치에서 매 시간 3km씩 이동 중이에요.
처음 발견 위치는 출발점에서 2km 떨어진 곳이었습니다.
"4시간 후 용의자 위치를 예측할 수 있을까?"`,
    situation: '용의자 이동 거리: 처음 2km 지점에서 시작해 매 시간 3km씩 멀어집니다.',
    question: 'x시간 후 이동 거리 y를 함수식으로 나타내고, 용의자가 14km 지점에 도달하는 시간은?',
    xLabel: '시간(x)',
    yLabel: '거리(y, km)',
    equation: 'y = 3x + 2',
    equationLabel: 'y = 3x + 2',
    slopeVal: 3,
    slopeDesc: '시간당 +3km (이동 속도)',
    interceptVal: 2,
    interceptDesc: '초기 위치 2km (y절편)',
    answer: 'y = 3x + 2\n14 = 3x + 2 → x = 4 → 4시간 후',
    whyMath: '수사관은 용의자 이동 경로·시간을 1차 함수로 분석해 위치를 예측하고 추적합니다.',
    insight: '경찰·국정원·사이버 수사대는 이동 경로·통신 패턴을 1차 함수로 분석해 수사에 활용합니다.',
    quiz: [
      { q: '5시간 후 용의자 위치는?', a: 'y = 3×5 + 2 = 17km', hint: 'x=5 대입' },
      { q: '용의자가 20km 지점에 도달하는 시간은?', a: '20 = 3x + 2 → x = 6시간', hint: '3x = 18' },
    ],
    jobs: ['수사관·탐정', '범죄 분석가', 'GPS 분석 전문가', '보안 연구원'],
  },
  {
    id: 'programmer',
    emoji: '💻',
    title: '프로그래머',
    category: 'IT',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `게임 개발자를 꿈꾸는 예은이는 첫 앱을 출시했습니다.
출시 첫날 다운로드 수가 1,000건이었고,
매일 500건씩 꾸준히 증가하고 있어요.
"며칠 후에 다운로드 10,000건을 달성할 수 있을까?"`,
    situation: '앱 다운로드 수: 첫날 1,000건에서 시작해 매일 500건씩 증가합니다.',
    question: 'x일 후 다운로드 수 y를 함수식으로 나타내고, 다운로드가 6,000건이 되는 날은?',
    xLabel: '일(x)',
    yLabel: '다운로드 수(y)',
    equation: 'y = 500x + 1000',
    equationLabel: 'y = 500x + 1000',
    slopeVal: 500,
    slopeDesc: '일당 +500건 (증가 속도)',
    interceptVal: 1000,
    interceptDesc: '초기 다운로드 1,000건 (y절편)',
    answer: 'y = 500x + 1000\n6000 = 500x + 1000 → x = 10 → 10일 후',
    whyMath: '개발자는 앱 다운로드·사용자 증가 데이터를 1차 함수로 분석해 서버 용량과 마케팅 전략을 결정합니다.',
    insight: '카카오·네이버·구글 같은 IT 기업의 데이터 엔지니어는 사용자 증가 패턴을 1차 함수로 모델링해 인프라를 설계합니다.',
    quiz: [
      { q: '7일 후 다운로드 수는?', a: 'y = 500×7 + 1000 = 4,500건', hint: 'x=7 대입' },
      { q: '다운로드가 11,000건이 되려면?', a: '11000 = 500x + 1000 → x = 20일', hint: '500x = 10000' },
    ],
    jobs: ['앱·게임 개발자', '데이터 엔지니어', '소프트웨어 아키텍트', 'AI 엔지니어'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🎬',
    title: '유튜버·크리에이터',
    example: '구독자·조회수 증가 추세를 1차 함수로 분석해 콘텐츠 전략 수립',
    formula: 'y = (주당 증가량)x + (초기 구독자)',
    salary: '연 3,000만~수억원',
    color: 'from-red-500 to-rose-500',
  },
  {
    icon: '⚽',
    title: '스포츠 데이터 분석가',
    example: '선수 성장 데이터를 1차 함수로 모델링해 훈련 계획 최적화',
    formula: 'y = (주당 성장량)x + (초기 성과)',
    salary: '연 3,500만~8,000만원',
    color: 'from-emerald-500 to-green-500',
  },
  {
    icon: '👨‍🍳',
    title: '셰프·식당 운영자',
    example: '재료비·수익을 1차 함수로 계산해 손익분기점·메뉴 가격 결정',
    formula: 'y = (단가)x + (기본 비용)',
    salary: '연 3,000만~1억원',
    color: 'from-orange-500 to-amber-500',
  },
  {
    icon: '👨‍⚕️',
    title: '의사·보건 연구원',
    example: '성장·회복 데이터를 1차 함수로 분석해 진단 기준 설정',
    formula: 'y = (월 성장량)x + (초기값)',
    salary: '연 6,000만~2억원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '🎤',
    title: '엔터테인먼트 PD',
    example: '연습생 성장 곡선을 1차 함수로 분석해 데뷔 시점 결정',
    formula: 'y = (주당 향상량)x + (초기 수준)',
    salary: '연 4,000만~1억원',
    color: 'from-pink-500 to-fuchsia-500',
  },
  {
    icon: '💻',
    title: '데이터 엔지니어',
    example: '앱 사용자 증가 패턴을 1차 함수로 모델링해 서버 인프라 설계',
    formula: 'y = (일당 증가량)x + (초기값)',
    salary: '연 5,000만~1억 5천만원',
    color: 'from-violet-500 to-purple-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🌱',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '식물이 처음 5cm에서 시작해 매일 3cm씩 자랍니다. x일 후 키 y를 함수식으로 나타내고, 20cm가 되는 날을 구하세요.',
    hint: 'y = 3x + 5. 20 = 3x + 5에서 x를 구하세요.',
    answer: 'y = 3x + 5, 20 = 3x + 5 → x = 5일',
    explanation: 'y = 3x + 5\n20 = 3x + 5\n3x = 15\nx = 5 → 5일 후에 20cm가 됩니다.',
  },
  {
    id: 'c2',
    emoji: '🚗',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '자동차가 출발 후 1시간이 지났을 때 이미 60km 지점에 있고, 이후 매 시간 80km씩 이동합니다. x시간 후 총 이동 거리 y를 구하고, 300km 지점에 도달하는 시간은?',
    hint: 'y = 80x + 60. 300 = 80x + 60에서 x를 구하세요.',
    answer: 'y = 80x + 60, 300 = 80x + 60 → x = 3시간',
    explanation: 'y = 80x + 60 (기울기 80, y절편 60)\n300 = 80x + 60\n80x = 240\nx = 3 → 3시간 후 300km 도달.',
  },
  {
    id: 'c3',
    emoji: '💰',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '한 달에 20,000원씩 저축하고 현재 통장에 50,000원이 있습니다. x개월 후 저축액 y를 함수식으로 나타내고, 저축액이 170,000원이 되는 달은?',
    hint: 'y = 20000x + 50000. 170000 = 20000x + 50000에서 x를 구하세요.',
    answer: 'y = 20000x + 50000, x = 6개월',
    explanation: 'y = 20000x + 50000\n170000 = 20000x + 50000\n20000x = 120000\nx = 6 → 6개월 후.',
  },
  {
    id: 'c4',
    emoji: '🔥',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '두 가게 A, B가 있습니다. A: 기본료 5,000원 + 개당 1,000원. B: 기본료 없음 + 개당 1,500원. 몇 개부터 A가 더 저렴한가요?',
    hint: 'A: y = 1000x + 5000, B: y = 1500x. 1000x + 5000 = 1500x에서 x를 구하세요.',
    answer: '10개 이상이면 A가 더 저렴합니다.',
    explanation: 'A: y = 1000x + 5000\nB: y = 1500x\n두 식이 같아지는 점: 1000x + 5000 = 1500x\n5000 = 500x → x = 10\n10개 미만: B가 저렴, 10개 이상: A가 저렴.',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '크리에이터': 'bg-red-100 text-red-700',
  '스포츠':     'bg-emerald-100 text-emerald-700',
  '요리':       'bg-orange-100 text-orange-700',
  '의료':       'bg-sky-100 text-sky-700',
  '연예':       'bg-pink-100 text-pink-700',
  '추리':       'bg-slate-100 text-slate-700',
  'IT':         'bg-violet-100 text-violet-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [xVal, setXVal] = useState('')
  const [yVal, setYVal] = useState('')
  const [mode, setMode] = useState<'findY' | 'findX'>('findY')
  const [result, setResult] = useState<{ steps: string[]; outputVal: number } | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const slope = scenario.slopeVal
  const intercept = scenario.interceptVal

  const reset = () => { setXVal(''); setYVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: 'findY' | 'findX') => { setMode(m); reset() }

  const calculate = () => {
    if (mode === 'findY') {
      const x = parseFloat(xVal)
      if (isNaN(x)) return
      const y = slope * x + intercept
      setResult({
        outputVal: y,
        steps: [
          `공식: ${scenario.equationLabel}`,
          `x = ${x} 대입`,
          `y = ${slope} × ${x} + ${intercept}`,
          `y = ${slope * x} + ${intercept} = ${y}`,
        ],
      })
    } else {
      const y = parseFloat(yVal)
      if (isNaN(y)) return
      const x = (y - intercept) / slope
      setResult({
        outputVal: x,
        steps: [
          `공식: ${scenario.equationLabel}`,
          `y = ${y} 대입`,
          `${y} = ${slope}x + ${intercept}`,
          `${slope}x = ${y - intercept}`,
          `x = ${(y - intercept)} ÷ ${slope} = ${x}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 0.01
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  return (
    <div className="card border-2 border-violet-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('findY')}
            className={`px-3 py-1.5 font-medium transition-colors ${
              mode === 'findY' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            x → y 구하기
          </button>
          <button
            onClick={() => handleModeChange('findX')}
            className={`px-3 py-1.5 font-medium transition-colors ${
              mode === 'findX' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            y → x 구하기
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3">
        <span className="text-xs text-slate-400 font-medium">함수식 </span>
        <span className="font-mono font-bold text-violet-700 text-sm ml-2">{scenario.equationLabel}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'findY' ? `🔢 x 값 입력 (${scenario.xLabel})` : `🔢 y 값 입력 (${scenario.yLabel})`}
          </label>
          <input
            type="number"
            value={mode === 'findY' ? xVal : yVal}
            onChange={e => { mode === 'findY' ? setXVal(e.target.value) : setYVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder={mode === 'findY' ? 'x 값 입력' : 'y 값 입력'}
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={mode === 'findY' ? !xVal : !yVal}
            className="px-5 py-2.5 rounded-xl font-bold text-sm bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400 transition-all"
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3">📝 풀이 과정</p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-violet-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-violet-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-violet-50 border-2 border-violet-300 rounded-xl px-5 py-4">
            <span className="text-3xl font-black text-violet-700">{result.outputVal}</span>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder="내 답 입력"
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-violet-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start gap-3 mb-3">
        <span className="text-2xl">{ch.emoji}</span>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-violet-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-violet-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function N4RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers',   label: '직업과 1차 함수', icon: <Briefcase size={14} /> },
    { id: 'challenges',label: '도전 문제',      icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── 헤더 ── */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 1차 함수</h1>
            <p className="text-sm text-slate-500">유튜버, 축구선수, 셰프, 의사, 아이돌, 탐정, 프로그래머 — 모두 1차 함수를 씁니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 1차 함수를 만나다!</p>
          <p className="text-sm opacity-90 mb-3">수학이 왜 필요한지 모르겠다고요? 당신이 꿈꾸는 직업이 바로 답합니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
            {[
              { icon: '🎬', job: '유튜버' },
              { icon: '⚽', job: '축구선수' },
              { icon: '👨‍🍳', job: '셰프' },
              { icon: '👨‍⚕️', job: '의사' },
              { icon: '🎤', job: '아이돌' },
              { icon: '🔍', job: '탐정' },
              { icon: '💻', job: '프로그래머' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center hover:bg-white/30 transition-colors cursor-pointer">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 탭 ── */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 탐색 ── */}
      {activeTab === 'scenarios' && (
        <>
          {/* 카테고리 필터 */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── 좌측: 시나리오 목록 ── */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => setSelected(scenario)}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold">{scenario.equationLabel}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* ── 우측: 시나리오 상세 ── */}
            <div className="lg:col-span-2 space-y-4">

              {/* 직업 헤더 카드 */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                {/* 이야기 */}
                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 1차 함수</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                {/* 수학적 상황 */}
                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                {/* 함수식 */}
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 함수식으로 나타내기</p>
                  <div className="mb-3">
                    <span className="text-2xl font-black text-violet-700 font-mono">{selected.equationLabel}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                      <p className="text-xs font-bold text-orange-700">기울기 (a = {selected.slopeVal})</p>
                      <p className="text-xs text-orange-600 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <p className="text-xs font-bold text-blue-700">y절편 (b = {selected.interceptVal})</p>
                      <p className="text-xs text-blue-600 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 문제 & 풀이 */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-violet-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 1차 함수인가?</span> {selected.whyMath}
                  </p>
                </div>
              </div>

              {/* 계산기 */}
              <ScenarioCalculator scenario={selected} />

              {/* 연습 문제 */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* 관련 직업 */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" /> 이 상황에서 1차 함수를 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* 실제 적용 */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['1차 함수', '기울기', 'y절편', '변화율', '실생활 모델링'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 핵심 개념 정리 */}
          <div className="mt-8 card bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 1차 함수 핵심 개념 정리</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '📐', title: '1차 함수', desc: 'y = ax + b\na: 기울기 (변화율)\nb: y절편 (초기값, x=0일 때 y값)\nx: 독립변수, y: 종속변수', color: 'text-violet-600' },
                { icon: '📈', title: '기울기 (a)', desc: 'x가 1 증가할 때 y의 변화량\na > 0: 우상향 (증가함수)\na < 0: 우하향 (감소함수)\n|a| 클수록 가파름', color: 'text-indigo-600' },
                { icon: '📊', title: 'y절편 (b)', desc: 'x = 0일 때 y값\n그래프가 y축과 만나는 점\n초기값·기본값을 의미\n실생활: 초기 구독자·기본 비용', color: 'text-purple-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업과 1차 함수 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 1차 함수를 사용하는 직업들</h2>
            <p className="text-sm text-violet-100">
              y = ax + b는 기상·의료·스포츠·IT·엔터테인먼트 등 수많은 직업의 핵심 분석 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 1차 함수가 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '머신러닝 기초', desc: '선형 회귀(Linear Regression)는 1차 함수 y=ax+b로 데이터를 예측하는 AI의 가장 기본 알고리즘입니다.', color: 'border-yellow-400' },
                { title: '비즈니스 분석', desc: '매출·비용·이익을 1차 함수로 모델링해 손익분기점과 성장 전략을 수립합니다.', color: 'border-rose-400' },
                { title: '과학·공학', desc: '속도·거리·시간, 전기 저항, 온도 변화 등 물리 현상의 대부분이 1차 함수로 모델링됩니다.', color: 'border-emerald-400' },
                { title: '데이터 시각화', desc: '트렌드 라인(추세선)은 1차 함수로 표현되어 데이터의 증가/감소 패턴을 한눈에 보여줍니다.', color: 'border-sky-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 1차 함수에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중2', topic: '1차 함수', active: true },
                { step: '중3', topic: '이차함수', active: false },
                { step: '고1', topic: '함수·지수·로그', active: false },
                { step: '고2', topic: '미적분 기초', active: false },
                { step: '대학', topic: '선형대수', active: false },
                { step: 'AI', topic: '머신러닝', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 5 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-violet-100">
              실제 상황에서 1차 함수를 직접 세우고 계산해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-violet-500" /> 1차 함수 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 함수식 세우기', desc: 'y = ax + b\na = 변화율(기울기)\nb = 초기값(y절편)\nx = 원인변수, y = 결과변수', example: 'y = 1200x + 500', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '② y 구하기', desc: 'x 값을 식에 대입\ny = a × (x값) + b\n단계별로 계산\n실생활 단위 확인!', example: 'x=3 → y = 1200×3+500 = 4100', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                { step: '③ x 구하기', desc: 'y 값을 식에 대입\nax = y - b\nx = (y - b) ÷ a\n역산으로 계산!', example: 'y=4100 → x=(4100-500)÷1200=3', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-violet-700">상황 파악 → 함수식 세우기 → x or y 구하기!</p>
              <p className="text-xs text-violet-500 mt-1">1차 함수의 흐름을 이해하면 어떤 실생활 문제도 수학으로 풀 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
